"use client";

import { graphql, useQuery } from "@opensea/graphql";
import { ImageStack, Spinner, Toast, ToastAvatar, ToastContent, ToastErrorIcon, ToastSide, ToastSuccessIcon, ToastTitle, useToasts } from "@opensea/ui-kit";
import { omit } from "es-toolkit";
import { useEffect, useMemo } from "react";
import { useTranslations } from "use-intl";
import { TransactionReceiptFragment } from "../../../components/TransactionReceipt/TransactionReceipt.fragment";
import { useRenderImage } from "../../../providers/RenderImageProvider";
import { FlowActions } from "../../../stores/flowStore";
const FETCH_INTERVAL = 500;
const query = graphql(`
    query CollectionSweepToastStatusQuery(
      $transactionIdentifiers: [TransactionIdentifier!]!
      $itemQuotes: [ItemQuoteInput!]!
    ) {
      sweepCollectionReceipt(
        transactionIdentifiers: $transactionIdentifiers
        itemQuotes: $itemQuotes
      ) {
        status
        itemReceipts {
          __typename
          item {
            id
          }
        }
        failedItemReceipts {
          __typename
          item {
            id
          }
        }
        ...TransactionReceipt
      }
    }
  `, [TransactionReceiptFragment]);
export function CollectionSweepToast({
  transactionIdentifiers,
  itemQuotes,
  itemImageMap,
  flow
}) {
  const t = useTranslations("wallet.CheckoutToast");
  const {
    renderImage
  } = useRenderImage();
  const {
    removeToast
  } = useToasts();
  const [result, reexecuteQuery] = useQuery({
    query,
    variables: {
      transactionIdentifiers: transactionIdentifiers.map(txn => ({
        chain: txn.chain,
        transactionHash: txn.hash
      })),
      itemQuotes: itemQuotes.map(quote => omit(quote, ["orderId"]))
    },
    pause: true
  });
  useEffect(function pollForReceipt() {
    if (result.fetching) {
      return;
    }
    const status = result.data?.sweepCollectionReceipt.status;
    if (status !== undefined && status !== "PENDING") {
      return;
    }
    if (transactionIdentifiers.length === 0) {
      return;
    }
    const timerId = setTimeout(() => {
      reexecuteQuery({
        requestPolicy: "network-only"
      });
    }, FETCH_INTERVAL);
    return () => clearTimeout(timerId);
  }, [result.fetching, reexecuteQuery, result.data?.sweepCollectionReceipt, transactionIdentifiers.length]);
  const receipt = result.data?.sweepCollectionReceipt;
  useEffect(function callOnSuccessWhenComplete() {
    if (receipt?.status === "SUCCESS" && flow) {
      FlowActions.onSuccess(flow);
    }
  }, [receipt?.status, flow]);
  useEffect(function removeToastWhenComplete() {
    let timerId_0;
    if (receipt && receipt.status !== "PENDING") {
      timerId_0 = setTimeout(removeToast, 1500);
    }
    return () => clearTimeout(timerId_0);
  }, [receipt, removeToast]);
  const imageUrls = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return receipt.itemReceipts.map(item_0 => item_0.item ? itemImageMap[item_0.item.id] : null);
      case "FAILED":
        return receipt.failedItemReceipts.map(item => item.item ? itemImageMap[item.item.id] : null);
      default:
        return [];
    }
  }, [receipt, itemImageMap]);
  const icon = useMemo(() => {
    switch (receipt?.status) {
      case "SUCCESS":
      case "PARTIAL_SUCCESS":
        return <ToastSuccessIcon />;
      case "FAILED":
        return <ToastErrorIcon />;
      case "PENDING":
      case undefined:
        return <Spinner size="sm" />;
      default:
        return Object.values(itemImageMap);
    }
  }, [receipt?.status, itemImageMap]);
  return <Toast>
      {imageUrls.length === 1 ? <ToastAvatar renderImage={renderImage} src={imageUrls[0] || ""} /> : <ImageStack overrides={{
      Avatar: {
        position: {
          center: {
            className: "rounded",
            renderImage
          },
          right: {
            className: "rounded"
          }
        }
      }
    }} size={44} srcs={imageUrls.slice(0, 2).map(imageUrl => imageUrl || "")} />}
      <ToastContent>
        <ToastTitle>
          {receipt?.status === "PENDING" || !receipt?.status ? t("pending") : t("title", {
          count: imageUrls.length
        })}
        </ToastTitle>
      </ToastContent>
      <ToastSide>{icon}</ToastSide>
    </Toast>;
}